import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { GeneralButton, TextInput, Picker } from "../../common/inputs";
import LoadingSpinner from "../../common/loaders/loadingSpinner";
import entityService from "../../../services/entityService";
import prospectService from "../../../services/prospectService";
import useApi from "../../../hooks/useApi";
import { toast } from "react-toastify";
import { FormGroup } from "../../common/containers/formContainer";
import { contactDesignations } from "../../../config/predefinedValues.json";

const normalizeYesNo = (value, fallback = "no") => {
  if (value === "yes" || value === "no") return value;
  if (value === true || value === 1 || value === "1") return "yes";
  if (value === false || value === 0 || value === "0") return "no";
  return fallback;
};

const EditProspectTeamMember = ({ data = {}, onCloseModal, onHandleReload }) => {
  const loadParentContactsApi = useApi(entityService.getEntityContactsList);
  const editProspectContactApi = useApi(prospectService.editProspectContact);

  const [bpContacts, setBpContacts] = useState([]);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    contact_id: "",
    first_name: "",
    last_name: "",
    email: "",
    phone_no: "",
    designation: "",
    status: "active",
    managing_bp_contact: null,
    enable_newsletter_subscription: "no",
    owner: "",
  });

  const newsletterOptions = [
    { label: "Subscribe", value: "yes" },
    { label: "Unsubscribe", value: "no" },
  ];

  const statusOptions = [
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  useEffect(() => {
    setForm({
      contact_id: data.contact_id || "",
      first_name: data.first_name || "",
      last_name: data.last_name || "",
      email: data.email || "",
      phone_no: data.phone_no || "",
      designation: data.designation || "",
      status: data.status || "active",
      managing_bp_contact: data.managing_bp_contact || null,
      enable_newsletter_subscription: normalizeYesNo(
        data.enable_newsletter_subscription,
        "no"
      ),
      owner: data.owner || "",
    });
    setErrors({});
  }, [data]);

  useEffect(() => {
    if (form.owner) {
      fetchManagingContacts(form.owner);
    }
  }, [form.owner]);

  const fetchManagingContacts = async (owner) => {
    try {
      const res = await loadParentContactsApi.request(owner);
      const list =
        res?.data?.rows?.map((item) => ({
          label: `${item.first_name} ${item.last_name}`,
          value: item.contact_id,
        })) || [];

      setBpContacts(list);
    } catch (error) {
      toast.error("Failed to load managing contacts");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.currentTarget;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSelectChange = (field, option) => {
    let value = option?.value ?? null;

    if (field === "enable_newsletter_subscription") {
      value = normalizeYesNo(value, "no");
    }

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const validate = () => {
    const e = {};

    if (!form.first_name?.trim()) e.first_name = "Please enter a First Name";
    if (!form.phone_no?.trim()) e.phone_no = "Please enter a Phone No.";
    if (!form.designation?.trim()) e.designation = "Please choose a Designation.";
    if (!form.status?.trim()) e.status = "Please choose a Status.";
    if (!form.managing_bp_contact) e.managing_bp_contact = "Please choose a Marketing Contact.";
    if (!form.enable_newsletter_subscription) {
      e.enable_newsletter_subscription = "Select Newsletter Option";
    }

    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);

      // show first error
      const firstError = Object.values(validationErrors)[0];
      toast.error(firstError);

      return;
    }

    const payload = {
      contact_id: form.contact_id,
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      phone_no: form.phone_no,
      designation: form.designation,
      status: form.status,
      managing_bp_contact: form.managing_bp_contact,
      enable_newsletter_subscription: normalizeYesNo(
        form.enable_newsletter_subscription,
        "no"
      ),
      clientAcEdit: true,
    };

    try {
      const response = await editProspectContactApi.request(payload);

      if (response?.data?.success || response?.success) {
        toast.success(
          response?.data?.message ||
            response?.message ||
            "Prospect contact updated successfully"
        );
        onCloseModal();
        onHandleReload();
      } else {
        toast.error(
          response?.data?.message || response?.message || "Update failed"
        );
      }
    } catch (err) {
      toast.error(err?.message || "Update failed");
    }
  };

  const selectedDesignation =
    contactDesignations.find((d) => d.value === form.designation) || null;

  const selectedStatus =
    statusOptions.find((s) => s.value === form.status) || null;

  const selectedContact =
    bpContacts.find((c) => c.value === form.managing_bp_contact) || null;

  const selectedNewsletter =
    newsletterOptions.find(
      (n) => n.value === normalizeYesNo(form.enable_newsletter_subscription, "no")
    ) || newsletterOptions[1];

  const isLoading =
    loadParentContactsApi.loading || editProspectContactApi.loading;

  return (
    <>
      {isLoading && <LoadingSpinner />}

      <Helmet>
        <title>Account | {global.config.title}</title>
      </Helmet>

      <div className="p-4">
        <form onSubmit={handleSubmit}>
          <TextInput
            name="first_name"
            label="First Name"
            value={form.first_name}
            onChange={handleInputChange}
            type="horizontal"
            mandatory
          />

          <TextInput
            name="last_name"
            label="Last Name"
            value={form.last_name}
            onChange={handleInputChange}
            type="horizontal"
          />

          <TextInput
            name="email"
            label="Email"
            value={form.email}
            onChange={handleInputChange}
            type="horizontal"
            readonly
            mandatory
          />

          <TextInput
            name="phone_no"
            label="Phone"
            value={form.phone_no}
            onChange={handleInputChange}
            type="horizontal"
            mandatory
          />

          

          <FormGroup
            label="Designation"
            type="horizontal"
            mandatory
            input={
              <Picker
                items={contactDesignations}
                selectedItem={selectedDesignation}
                onSelectItem={(option) =>
                  handleSelectChange("designation", option)
                }
              />
            }
          />
         

          <FormGroup
            label="Status"
            mandatory
            type="horizontal"
            input={
              <Picker
                items={statusOptions}
                selectedItem={selectedStatus}
                onSelectItem={(option) =>
                  handleSelectChange("status", option)
                }
              />
            }
          />
         

          <FormGroup
            label="Managing Contact"
            mandatory
            type="horizontal"
            input={
              <Picker
                items={bpContacts}
                selectedItem={selectedContact}
                onSelectItem={(option) =>
                  handleSelectChange("managing_bp_contact", option)
                }
              />
            }
          />
        

          <FormGroup
            label="Newsletter"
            mandatory
            type="horizontal"
            input={
              <Picker
                items={newsletterOptions}
                selectedItem={selectedNewsletter}
                onSelectItem={(option) =>
                  handleSelectChange(
                    "enable_newsletter_subscription",
                    option
                  )
                }
              />
            }
          />
        

           <div className="row">
               <div className="col-lg-12">
                  <p className="text-xs text-danger">* Please be aware that any changes made to the settings will be automatically reflected in the HubSpot profile.</p>
              </div>
              <div className="col-lg-12">
                <GeneralButton
                  className="btn btn-primary btn-sm float-right mr-2"
                  faIcon="fa fa-save"
                  name="Update"
                ></GeneralButton>
              </div>
            </div>
        </form>
      </div>
    </>
  );
};

export default EditProspectTeamMember;